import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from "react";
import type { Belegvorschau } from "@kundeninfo/api-client";
import { Blatt, BlattElement } from "@kundeninfo/ui/me-design";
import type { BlattLinienGruppe } from "@kundeninfo/ui/me-design";
import type { FussLinien } from "./layoutEntwurf.js";

/**
 * Das BLATT (Spec, Oberfläche, Mitte): die Seitenbilder der Engine — das exakte Blatt,
 * nicht eine Nachbildung. Seit ME-Etappe 4 (P10) ist jede Seite ein `me-blatt` (210 × 297
 * mm) mit dem Seitenbild im Slot `hintergrund`; die Rahmen (Logo, Grafiken, Unterschrifts-
 * platz) sind `me-blatt-element`e. Zoom „Breite“ (in die Spalte) oder „100 %“ (1 mm =
 * 96/25,4 px), Hilfslinien, Auswahl, Ziehen, Griffe und Pfeiltasten bringt das Element mit.
 *
 * Die Lage gehört dem Editor: `objekte(seite)` liefert die Rahmen einer Seite in mm,
 * `onVerschoben` meldet eine neue Lage (nach Ende von Zug, Griff oder Taste); übernimmt der
 * Editor sie nicht, zieht das Element auf die alte zurück (kontrolliert). `gewaehlt` ist
 * die Auswahl über alle Seiten (die Kennung des OBJEKTS — dasselbe Logo steht auf Seite 1
 * und den Folgeseiten). Hilfslinien gehen nie ins PDF.
 */

export type BlattLage = { xMm: number; yMm: number; widthMm: number; heightMm: number };

/** Ein Rahmen im Blatt — Lage und Maß in mm ab der linken oberen Blattkante. */
export type BlattObjekt = {
  /** Kennung des Objekts (Grafik-ID, „logo“) — gleich auf allen Seiten. */
  id: string;
  /** Name am Rahmen (Namensschild, zugänglicher Name). */
  beschriftung: string;
  xMm: number;
  yMm: number;
  breiteMm: number;
  hoeheMm: number;
  verhaeltnisHalten?: boolean;
  minBreiteMm?: number;
  minHoeheMm?: number;
  /** Klassen am Element (Optik der Anwendung, z. B. die Ruhe-Markierung). */
  klasse?: string;
  /** Zeigt das Element während eines Zugs und solange `schwebend` (das Seitenbild zeigt
   *  das Objekt noch am alten Ort — Entscheid 25). */
  schatten?: ReactNode;
  /** Die neue Lage ist gemeldet, aber noch nicht gerendert (Serverweg läuft). */
  schwebend?: boolean;
};

export type BlattProps = {
  vorschau: Belegvorschau | null;
  laedt: boolean;
  fehler: string | null;
  zoom: "breite" | "voll";
  /** Zeigt die Seitenränder und die Kopf-/Fußlinien (Gruppe „Hilfslinien“). Das Einrasten an
   *  Kanten, Mitte und Rändern gilt immer — auch ohne Anzeige. */
  hilfslinien: boolean;
  /** Lagen der Spaltenlinien in % der Satzspiegelbreite; null = aus (Entscheid 64). */
  spaltenlinien?: readonly number[] | null | undefined;
  raender: { oben: number; rechts: number; unten: number; links: number };
  /** Unterkante des Kopfbereichs je Seitentyp in mm (Belegdesign Etappe 2); null = aus,
   *  ein Wert null = nicht gesetzt (keine Linie). */
  kopfbereich?: { erste: number | null; folge: number | null } | null | undefined;
  /** Die Hilfslinien des Fußes (Belegdesign Etappe 2): Spaltengrenzen und Oberkante des
   *  Fußbereichs je Seitentyp; null = aus. */
  fuss?: FussLinien | null | undefined;
  objekte?: ((seite: number) => readonly BlattObjekt[]) | undefined;
  /** Kennung des gewählten Objekts; null = keines. */
  gewaehlt?: string | null | undefined;
  /** Nichts verschiebbar (Fremder Stand, laufender Weg); Auswahl und Fokus bleiben. */
  gesperrt?: boolean | undefined;
  onGewaehlt?: ((id: string | null) => void) | undefined;
  onVerschoben?: ((id: string, lage: BlattLage) => void) | undefined;
  /** Die Zwischenlage während eines Zugs oder Griffs (je Frame, ohne dass das Element
   *  Attribute ändert); `null` beim Ende (Loslassen, Abbruch). Nur zum Mitlaufen der
   *  Registerfelder — der Entwurf wird erst mit `onVerschoben` geschrieben. */
  onZieht?: ((id: string, lage: BlattLage | null) => void) | undefined;
};

export const BLATT_BREITE_MM = 210;
export const BLATT_HOEHE_MM = 297;

/** DOM-Kennung eines Objekts auf einer Seite: `s<Seite>--<Objekt>` (eindeutig im Dokument). */
const TRENNER = "--";
function domId(seite: number, id: string): string {
  return `s${seite}${TRENNER}${id}`;
}
function objektId(dom: string): string {
  const i = dom.indexOf(TRENNER);
  return i < 0 ? dom : dom.slice(i + TRENNER.length);
}

/** Das Blatt-Element, auf das ein Zeigerereignis zielt (über die Schattengrenze). */
function zielElement(e: ReactPointerEvent<HTMLElement>): HTMLElement | null {
  const pfad = e.nativeEvent.composedPath();
  return (pfad.find((k) => k instanceof HTMLElement && k.localName === "me-blatt-element")
    ?? null) as HTMLElement | null;
}

export function LayoutBlatt(p: BlattProps): ReactElement {
  // Welches Objekt gerade gezogen wird (für den Schatten): vom Druck bis zum Loslassen.
  const [zieht, setZieht] = useState<string | null>(null);
  const aufraeumen = useRef<(() => void) | null>(null);
  useEffect(() => () => { aufraeumen.current?.(); }, []);

  function zugBeginn(e: ReactPointerEvent<HTMLElement>): void {
    if (p.gesperrt === true || e.button !== 0 || aufraeumen.current !== null) return;
    const element = zielElement(e);
    if (element === null || element.id === "" || element.hasAttribute("gesperrt")) return;
    const zeiger = e.pointerId;
    const ende = (ev: PointerEvent): void => {
      if (ev.pointerId !== zeiger) return;
      aufraeumen.current?.();
    };
    // Escape bricht den Zug im Element ab (me-blatt), ohne ein Ende zu melden — weder ein
    // letztes me-zieht noch pointercancel. Ohne diesen Horcher zeigten die Registerfelder
    // bis zum Loslassen die verworfene Zwischenlage (Gesamtreview Etappe 4, H10). Er hängt
    // wie der des Elements in der Einfangphase am Fenster; dessen stopPropagation hält
    // einen zweiten Horcher am selben Ziel nicht auf.
    const escape = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") aufraeumen.current?.();
    };
    aufraeumen.current = () => {
      window.removeEventListener("pointerup", ende);
      window.removeEventListener("pointercancel", ende);
      window.removeEventListener("keydown", escape, true);
      aufraeumen.current = null;
      setZieht(null);
      p.onZieht?.(objektId(element.id), null);
    };
    window.addEventListener("pointerup", ende);
    window.addEventListener("pointercancel", ende);
    window.addEventListener("keydown", escape, true);
    setZieht(objektId(element.id));
  }

  const satzspiegel = BLATT_BREITE_MM - p.raender.links - p.raender.rechts;
  const spaltenMm = p.spaltenlinien == null ? undefined
    : p.spaltenlinien.map((x) => Math.round((p.raender.links + x * satzspiegel / 100) * 100) / 100);

  return (
    <div className={`lay__blatt${p.laedt ? " lay__blatt--laedt" : ""}${p.zoom === "voll" ? " lay__blatt--voll" : ""}`}
      aria-busy={p.laedt} onPointerDown={zugBeginn}>
      {p.fehler !== null && <p className="lay__blattfehler" role="alert">{p.fehler}</p>}
      {p.vorschau === null && p.fehler === null && (
        <p className="lay__blattleer">
          {p.laedt ? "Das Blatt wird gerendert …"
            : "Noch keine Vorschau — „Aktualisieren“ rendert das Blatt."}
        </p>
      )}
      {(p.vorschau?.seiten ?? []).map((seite) => {
        const typ = seite.nummer === 1 ? "erste" : "folge";
        const kopf = p.kopfbereich == null ? null : p.kopfbereich[typ];
        const fussKante = p.fuss == null ? null : p.fuss.oberkanteMm[typ];
        const fussSpalten = fussKante === null || p.fuss == null || p.fuss.spaltenMm.length === 0
          ? undefined : p.fuss.spaltenMm;
        // Liniengruppen (me-design 219008f): ohne Attribut wird nichts gezeichnet; Kopf-/Fußlinien
        // erscheinen, wenn der Editor sie liefert (Hilfslinien oder Register Marke, Entscheid 75). Die Ränder
        // gehen immer an das Element (Einrasten auch ohne Anzeige), gezeichnet werden sie nur
        // mit der Gruppe `raender`.
        const gruppen: BlattLinienGruppe[] = [];
        if (p.hilfslinien) gruppen.push("raender");
        if (spaltenMm !== undefined) gruppen.push("spalten");
        if (kopf !== null) gruppen.push("kopf");
        if (fussKante !== null) gruppen.push("fuss");
        if (fussSpalten !== undefined) gruppen.push("fussspalten");
        const objekte = p.objekte?.(seite.nummer) ?? [];
        const gewaehlt = p.gewaehlt != null && objekte.some((o) => o.id === p.gewaehlt)
          ? domId(seite.nummer, p.gewaehlt) : null;
        return (
          <Blatt key={seite.nummer} className={`lay__seite lay__seite--${typ}`}
            breite={BLATT_BREITE_MM} hoehe={BLATT_HOEHE_MM}
            zoom={p.zoom === "voll" ? "100" : "breite"}
            raender={p.raender}
            {...(spaltenMm === undefined ? {} : { spaltenlinien: spaltenMm })}
            {...(fussSpalten === undefined ? {} : { fussspalten: fussSpalten })}
            hilfslinien={gruppen} kopflinie={kopf} fusslinie={fussKante}
            gewaehlt={gewaehlt} gesperrt={p.gesperrt === true}
            hintergrund={
              <img className="lay__seitenbild" src={`data:image/png;base64,${seite.png}`}
                width={seite.breite} height={seite.hoehe} alt={`Seite ${seite.nummer}`}
                draggable={false} />
            }
            onGewaehlt={(d) => p.onGewaehlt?.(d.id === null ? null : objektId(d.id))}
            onZieht={(d) => {
              if (aufraeumen.current !== null) p.onZieht?.(objektId(d.id), {
                xMm: d.xMm, yMm: d.yMm, widthMm: d.widthMm, heightMm: d.heightMm });
            }}
            onVerschoben={(d) => p.onVerschoben?.(objektId(d.id), {
              xMm: d.xMm, yMm: d.yMm, widthMm: d.widthMm, heightMm: d.heightMm })}>
            {objekte.map((o) => (
              <BlattElement key={o.id} id={domId(seite.nummer, o.id)}
                beschriftung={o.beschriftung} x={o.xMm} y={o.yMm} breite={o.breiteMm}
                hoehe={o.hoeheMm} verhaeltnisHalten={o.verhaeltnisHalten === true}
                // Der gewählte Rahmen trägt den Rahmen des Elements — die Ruhe-Markierung der
                // Anwendung tritt dann zurück (keine Doppellinie).
                {...(o.klasse === undefined ? {} : {
                  className: o.id === p.gewaehlt ? `${o.klasse} lay__rahmen--gewaehlt` : o.klasse })}
                {...(o.minBreiteMm === undefined ? {} : { minBreite: o.minBreiteMm })}
                {...(o.minHoeheMm === undefined ? {} : { minHoehe: o.minHoeheMm })}>
                {(zieht === o.id || o.schwebend === true) && o.schatten}
              </BlattElement>
            ))}
          </Blatt>
        );
      })}
    </div>
  );
}
